import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Toast } from "primereact/toast";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  deleteCompanyService,
  getAllCompaniesService,
  updateCompanyService,
} from "../../services/Company/CompanyServices";
import { showError, showSuccess } from "../Toast/Toast.functions";
import AdminPageHeader from "./AdminPageHeader";

interface AdminCompany {
  _id: string;
  name: string;
  description: string;
  vat: string;
  address: { label?: string; lat?: string; lon?: string };
  logo?: any;
  pictures?: any[];
  email: string;
  phone: string;
  values: string;
  website?: string;
  companyType: number;
  products?: any[];
}

const COMPANY_TYPE_OPTIONS = [
  { value: 0, labelKey: "companyTypeNone" },
  { value: 1, labelKey: "products" },
  { value: 2, labelKey: "services" },
  { value: 3, labelKey: "productsServices" },
];

const AdminCompanies = () => {
  const { t } = useTranslation(["common"]);
  const toast = useRef<Toast>(null);

  const [companies, setCompanies] = useState<AdminCompany[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [globalFilterValue, setGlobalFilterValue] = useState("");

  const [editingCompany, setEditingCompany] = useState<AdminCompany | null>(null);
  const [deletingCompany, setDeletingCompany] = useState<AdminCompany | null>(null);

  const companyTypeLabel = (companyType: number) => {
    const option = COMPANY_TYPE_OPTIONS.find((o) => o.value === companyType);
    return option ? t(`common:${option.labelKey}`) : "-";
  };

  const loadCompanies = () => {
    setLoading(true);
    getAllCompaniesService()
      .then((res: any) => setCompanies(res.data ?? []))
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadCompanies();
  }, []);

  const filteredCompanies = companies.filter((c) => {
    const search = globalFilterValue.trim().toLowerCase();
    if (!search) return true;
    return (
      c.name?.toLowerCase().includes(search) ||
      c.vat?.toLowerCase().includes(search) ||
      c.email?.toLowerCase().includes(search)
    );
  });

  const submitEdit = () => {
    if (!editingCompany) return;
    updateCompanyService({
      _id: editingCompany._id,
      name: editingCompany.name,
      description: editingCompany.description,
      vat: editingCompany.vat,
      address: editingCompany.address,
      logo: editingCompany.logo,
      pictures: editingCompany.pictures ?? [],
      email: editingCompany.email,
      phone: editingCompany.phone,
      values: editingCompany.values,
      website: editingCompany.website,
      companyType: editingCompany.companyType,
    })
      .then(() => {
        showSuccess(toast, t("common:Save"));
        setEditingCompany(null);
        loadCompanies();
      })
      .catch(() => showError(toast, t("common:genericError")));
  };

  const confirmDelete = () => {
    if (!deletingCompany) return;
    deleteCompanyService({ _id: deletingCompany._id })
      .then(() => {
        setCompanies((prev) => prev.filter((c) => c._id !== deletingCompany._id));
        setDeletingCompany(null);
      })
      .catch(() => showError(toast, t("common:genericError")));
  };

  return (
    <div className="admin-page">
      <Toast ref={toast} position="top-right" />
      <AdminPageHeader title={t("common:companies")} />

      <div className="admin-page-body">
      <div className="p-input-icon-left mb-3">
        <i className="pi pi-search" />
        <InputText
          value={globalFilterValue}
          onChange={(e) => setGlobalFilterValue(e.target.value)}
          placeholder="Keyword Search"
        />
      </div>

      <DataTable
        value={filteredCompanies}
        loading={loading}
        paginator
        rows={20}
        dataKey="_id"
        sortField="name"
        sortOrder={1}
        emptyMessage={t("common:noResults")}
      >
        <Column field="name" header={t("common:Name")} sortable />
        <Column field="vat" header={t("common:vat")} sortable />
        <Column
          field="companyType"
          header={t("common:type")}
          body={(row: AdminCompany) => companyTypeLabel(row.companyType)}
        />
        <Column field="email" header={t("common:Email")} />
        <Column field="phone" header={t("common:Phone")} />
        <Column
          field="products"
          header={t("common:products")}
          body={(row: AdminCompany) => row.products?.length ?? 0}
        />
        <Column
          body={(row: AdminCompany) => (
            <div className="admin-row-actions">
              <Button
                icon="pi pi-pencil"
                className="p-button-rounded p-button-text"
                onClick={() => setEditingCompany({ ...row })}
              />
              <Button
                icon="pi pi-trash"
                className="p-button-rounded p-button-warning"
                onClick={() => setDeletingCompany(row)}
              />
            </div>
          )}
        />
      </DataTable>
      </div>

      <Dialog
        header={editingCompany?.name}
        visible={editingCompany != null}
        onHide={() => setEditingCompany(null)}
        style={{ width: "40vw" }}
        footer={
          <div className="flex justify-content-space-between">
            <Button className="general-button" onClick={() => setEditingCompany(null)}>
              {t("common:undo")}
            </Button>
            <Button className="general-button" onClick={submitEdit}>
              {t("common:Save")}
            </Button>
          </div>
        }
      >
        {editingCompany && (
          <div className="p-fluid">
            <div className="field">
              <label>{t("common:Name")}</label>
              <InputText
                value={editingCompany.name}
                onChange={(e) =>
                  setEditingCompany({ ...editingCompany, name: e.target.value })
                }
              />
            </div>
            <div className="field">
              <label>{t("common:Description")}</label>
              <InputTextarea
                rows={3}
                value={editingCompany.description}
                onChange={(e) =>
                  setEditingCompany({ ...editingCompany, description: e.target.value })
                }
              />
            </div>
            <div className="field">
              <label>{t("common:vat")}</label>
              <InputText
                value={editingCompany.vat}
                onChange={(e) =>
                  setEditingCompany({ ...editingCompany, vat: e.target.value })
                }
              />
            </div>
            <div className="field">
              <label>{t("common:Email")}</label>
              <InputText
                value={editingCompany.email}
                onChange={(e) =>
                  setEditingCompany({ ...editingCompany, email: e.target.value })
                }
              />
            </div>
            <div className="field">
              <label>{t("common:Phone")}</label>
              <InputText
                value={editingCompany.phone}
                onChange={(e) =>
                  setEditingCompany({ ...editingCompany, phone: e.target.value })
                }
              />
            </div>
            <div className="field">
              <label>{t("common:website")}</label>
              <InputText
                value={editingCompany.website ?? ""}
                onChange={(e) =>
                  setEditingCompany({ ...editingCompany, website: e.target.value })
                }
              />
            </div>
            <div className="field">
              <label>{t("common:values")}</label>
              <InputTextarea
                rows={2}
                value={editingCompany.values}
                onChange={(e) =>
                  setEditingCompany({ ...editingCompany, values: e.target.value })
                }
              />
            </div>
            <div className="field">
              <label>{t("common:type")}</label>
              <Dropdown
                value={editingCompany.companyType}
                options={COMPANY_TYPE_OPTIONS.map((o) => ({
                  value: o.value,
                  label: t(`common:${o.labelKey}`),
                }))}
                onChange={(e) =>
                  setEditingCompany({ ...editingCompany, companyType: e.value })
                }
              />
            </div>
            <div className="field">
              <label>{t("common:Address")}</label>
              <InputText
                value={editingCompany.address?.label ?? ""}
                onChange={(e) =>
                  setEditingCompany({
                    ...editingCompany,
                    address: { ...editingCompany.address, label: e.target.value },
                  })
                }
              />
            </div>
          </div>
        )}
      </Dialog>

      <Dialog
        onHide={() => setDeletingCompany(null)}
        visible={deletingCompany != null}
        closable={false}
        style={{ maxWidth: "50vh" }}
        footer={
          <div className="flex justify-content-space-between">
            <Button className="general-button" onClick={() => setDeletingCompany(null)}>
              {t("common:No")}
            </Button>
            <Button className="general-button" onClick={confirmDelete}>
              {t("common:Yes")}
            </Button>
          </div>
        }
      >
        {deletingCompany?.name}
      </Dialog>
    </div>
  );
};

export default AdminCompanies;
